import { ICON_PROPS, cleanSubject, formatRelative, friendlyAiError, postToHost, safeParse, shortDate, tierColor } from './shared.js';
import { useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api } from '../../api.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — cross-panel widgets (banners, menus, snooze/reply/attachment/case widgets, command bar),
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export function SearchScopeBanner(): React.JSX.Element | null {
  const [stats, setStats] = useState<Awaited<ReturnType<typeof api.addinStats>> | null>(null);
  useEffect(() => {
    api.addinStats().then(setStats).catch(() => {});
  }, []);
  if (!stats) return null;
  const folders = stats.by_folder?.length ?? 0;
  const total = stats.total_indexed ?? 0;
  const hasIndex = total > 0;
  return (
    <Alert
      color={folders <= 1 ? 'orange' : 'blue'}
      variant="light"
      icon={<IconSearch size={14} />}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Text size="xs">
          {hasIndex ? (
            <>
              Searching <b>{total.toLocaleString()}</b> emails across <b>{folders}</b> folder{folders === 1 ? '' : 's'}.
              {folders <= 1 && ' To search every Outlook folder + Sent, index more.'}
            </>
          ) : (
            <>No emails indexed yet — open <b>Mail sources</b> and click "Index every Inbox + Sent".</>
          )}
        </Text>
        {folders <= 1 && (
          <Button
            size="compact-xs"
            variant="filled"
            onClick={() => postToHost({ type: 'start_index_all_mailboxes', folder_kinds: ['inbox', 'sent'] })}
          >
            Index everything
          </Button>
        )}
      </Group>
    </Alert>
  );
}

// Bulk action: add selected search results to a category. Round 43.
// Loads categories on demand (when menu opens) so it's cheap when unused.
export function BulkAddToCategoryMenu({
  selectedIds,
  onApplied,
}: {
  selectedIds: Set<string>;
  onApplied: () => void;
}): React.JSX.Element {
  const [cats, setCats] = useState<Awaited<ReturnType<typeof api.addinCategoriesList>> | null>(null);
  const [loading, setLoading] = useState(false);

  async function ensureLoaded() {
    if (cats || loading) return;
    setLoading(true);
    try { setCats(await api.addinCategoriesList()); } finally { setLoading(false); }
  }

  async function applyCategory(catId: string, catName: string) {
    if (selectedIds.size === 0) return;
    try {
      const r = await api.addinCategoryApply(catId, { message_ids: [...selectedIds] });
      notifications.show({ message: `Added ${r.applied} email${r.applied === 1 ? '' : 's'} to "${catName}".` });
      onApplied();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    }
  }

  return (
    <Menu onOpen={ensureLoaded}>
      <Menu.Target>
        <Button size="compact-xs" variant="default">
          Add to category…
        </Button>
      </Menu.Target>
      <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
        {loading && <Menu.Item disabled>Loading…</Menu.Item>}
        {cats && cats.categories.length === 0 && (
          <Menu.Item disabled>No categories yet — create one in Discover → Organize.</Menu.Item>
        )}
        {cats && cats.categories.map((c) => (
          <Menu.Item
            key={c.id}
            leftSection={<Badge size="xs" color={c.color || 'blue'}>{c.email_count}</Badge>}
            onClick={() => applyCategory(c.id, c.name)}
          >
            {c.name}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

// Per-row action menu — change tier, ignore (snooze 1y), delete. Round 42:
// the user wants to override the AI classifier inline ("this isn't P0") or
// banish a message they don't care about.
export function EmailRowMenu({
  message_id,
  currentTier,
  onChanged,
}: {
  message_id: string;
  currentTier: string | null;
  onChanged?: () => void;
}): React.JSX.Element {
  async function setTier(tier: string | null) {
    try {
      await api.addinSetTier(message_id, tier);
      notifications.show({
        message: tier ? `Set priority to ${tier}.` : 'Cleared priority.',
      });
      onChanged?.();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    }
  }
  async function ignore() {
    const oneYearMs = 365 * 24 * 60 * 60 * 1000;
    try {
      await api.addinSnooze(message_id, new Date(Date.now() + oneYearMs).toISOString(), 'Ignored');
      notifications.show({ message: 'Hidden for a year.' });
      onChanged?.();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    }
  }
  function deleteEmail() {
    if (!confirm('Delete this email permanently? This moves it to Outlook Deleted Items.')) return;
    postToHost({ type: 'delete_email', message_id });
    notifications.show({ message: 'Deleted from Outlook.' });
    onChanged?.();
  }
  const tiers: Array<'P0' | 'P1' | 'P2' | 'P3' | 'FYI'> = ['P0', 'P1', 'P2', 'P3', 'FYI'];
  return (
    <Menu position="bottom-end" withArrow shadow="md">
      <Menu.Target>
        <ActionIcon size="sm" variant="subtle" color="gray">
          <IconCommand size={14} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Set priority</Menu.Label>
        {tiers.map((t) => (
          <Menu.Item
            key={t}
            leftSection={<Badge size="xs" color={tierColor(t)}>{t}</Badge>}
            disabled={currentTier === t}
            onClick={() => setTier(t)}
          >
            {t === 'P0' ? 'Critical' : t === 'P1' ? 'High' : t === 'P2' ? 'Medium' : t === 'P3' ? 'Low' : 'FYI'}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item leftSection={<IconArchive size={14} stroke={1.5} />} onClick={ignore}>
          Ignore (hide for 1 year)
        </Menu.Item>
        <Menu.Item color="red" leftSection={<IconTrash size={14} stroke={1.5} />} onClick={deleteEmail}>
          Delete from Outlook
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

// Folder enumeration tends to take a few seconds; if it's silent past 10s,
// something is genuinely wrong (PSTs offline, Exchange password prompt, etc).
export function FolderLoadingState(): React.JSX.Element {
  const [tooLong, setTooLong] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTooLong(true), 10_000);
    return () => clearTimeout(t);
  }, []);
  if (tooLong) {
    return (
      <Alert color="orange" variant="light">
        Still waiting on Outlook to list folders. Check Outlook for a password prompt or PST that
        needs to be unlocked. Click "Reload list" to retry.
      </Alert>
    );
  }
  return (
    <Group gap="xs">
      <Loader size="xs" />
      <Text c="dimmed" size="sm">Asking Outlook for folder list…</Text>
    </Group>
  );
}

// Small badge that surfaces when private (Ollama) mode is active. Polls
// settings on mount so the user always knows where their LLM calls are
// going. Refreshes when they toggle in Settings.
export function PrivateModeBadge(): React.JSX.Element | null {
  const [active, setActive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      api.addinSettingsGet()
        .then((r) => { if (!cancelled) setActive(r.private_mode === 1); })
        .catch(() => {});
    };
    check();
    // 90s poll. private_mode is user-toggled in Settings — never auto-changes —
    // so the badge can lag by ~1.5 min without UX impact. With 3 concurrent
    // addin clients the previous 30s poll burned ~6 settings GETs/min in
    // aggregate; 90s drops that to ~2/min.
    const id = setInterval(check, 90_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (!active) return null;
  return (
    <Tooltip label="Private mode ON — LLM calls route to local Ollama. No external network calls.">
      <Badge color="violet" size="xs" variant="filled" leftSection={<IconShield size={10} />}>
        PRIVATE
      </Badge>
    </Tooltip>
  );
}

// Triage queue banner — message_ids pushed from Risk feed, surfaced here.
export function TriageQueueBanner(): React.JSX.Element | null {
  const [queue, setQueue] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('addin_triage_queue') ?? '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    const handler = () => {
      try { setQueue(JSON.parse(localStorage.getItem('addin_triage_queue') ?? '[]')); }
      catch { setQueue([]); }
    };
    window.addEventListener('ai-outlook:triage-update', handler);
    return () => window.removeEventListener('ai-outlook:triage-update', handler);
  }, []);
  function removeOne(mid: string) {
    const next = queue.filter((m) => m !== mid);
    if (next.length === 0) localStorage.removeItem('addin_triage_queue');
    else localStorage.setItem('addin_triage_queue', JSON.stringify(next));
    setQueue(next);
  }
  if (queue.length === 0) return null;
  return (
    <Alert color="blue" variant="light" icon={<IconBolt size={14} />}
      withCloseButton
      onClose={() => {
        localStorage.removeItem('addin_triage_queue');
        setQueue([]);
      }}>
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Text size="xs">
            <b>{queue.length}</b> item{queue.length === 1 ? '' : 's'} pushed from Risk feed for triage.
          </Text>
          <Group gap={4}>
            <Button size="compact-xs" variant="default" onClick={() => {
              for (const mid of queue) postToHost({ type: 'open_in_outlook', message_id: mid });
              localStorage.removeItem('addin_triage_queue');
              setQueue([]);
            }}>
              Open all
            </Button>
            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => {
              localStorage.removeItem('addin_triage_queue');
              setQueue([]);
            }}>
              Clear
            </Button>
          </Group>
        </Group>
        {/* Per-item remove chips — short ids so they fit */}
        <Group gap={4} wrap="wrap">
          {queue.slice(0, 12).map((mid) => (
            <Badge key={mid} size="xs" variant="light" color="blue"
              rightSection={
                <ActionIcon size={12} variant="transparent" color="blue" onClick={() => removeOne(mid)}>
                  <IconX size={9} />
                </ActionIcon>
              }
              style={{ cursor: 'pointer' }}
              onClick={() => postToHost({ type: 'open_in_outlook', message_id: mid })}
            >
              {mid.slice(0, 12)}…
            </Badge>
          ))}
          {queue.length > 12 && (
            <Badge size="xs" variant="dot">+{queue.length - 12} more</Badge>
          )}
        </Group>
      </Stack>
    </Alert>
  );
}

// Generic one-time onboarding tip — dismiss persists in localStorage.
export function OnboardingTip({
  tabKey,
  title,
  body,
}: {
  tabKey: string;
  title: string;
  body: string;
}): React.JSX.Element | null {
  const storeKey = `addin_onboard_${tabKey}_dismissed`;
  const [shown, setShown] = useState(() => {
    try { return localStorage.getItem(storeKey) !== '1'; } catch { return true; }
  });
  if (!shown) return null;
  return (
    <Alert color="grape" variant="light" icon={<IconSparkles size={14} />}
      title={title}
      withCloseButton
      onClose={() => {
        setShown(false);
        try { localStorage.setItem(storeKey, '1'); } catch { /* ignore */ }
      }}>
      <Text size="xs">{body}</Text>
    </Alert>
  );
}


export function SnoozeButton({
  onSnooze,
}: {
  onSnooze: (until: string, reason?: string) => void;
}): React.JSX.Element {
  function nextHour(h: number) {
    return new Date(Date.now() + h * 3600 * 1000).toISOString();
  }
  function tomorrowMorning() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  function nextMonday() {
    const d = new Date();
    const daysUntilMon = (8 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + daysUntilMon);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  function customDate() {
    const v = prompt('Snooze until (YYYY-MM-DD HH:MM):');
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) {
      notifications.show({ color: 'red', message: 'Invalid date format.' });
      return null;
    }
    return d.toISOString();
  }
  return (
    <Menu position="bottom-end" withArrow>
      <Menu.Target>
        <Button size="xs" variant="default" leftSection={<IconFlag {...ICON_PROPS} />}>
          Snooze
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Hide until…</Menu.Label>
        <Menu.Item onClick={() => onSnooze(nextHour(1))}>1 hour</Menu.Item>
        <Menu.Item onClick={() => onSnooze(nextHour(3))}>3 hours</Menu.Item>
        <Menu.Item onClick={() => onSnooze(tomorrowMorning())}>Tomorrow 9 AM</Menu.Item>
        <Menu.Item onClick={() => onSnooze(nextMonday())}>Next Monday 9 AM</Menu.Item>
        <Menu.Item onClick={() => onSnooze(nextHour(7 * 24))}>1 week</Menu.Item>
        <Menu.Divider />
        <Menu.Item
          onClick={() => {
            const c = customDate();
            if (c) onSnooze(c);
          }}
        >
          Custom date…
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

// Phase 6b — ReplyBriefCard: contextual brief about this person/thread
// before you write the reply. Surfaces open promises, recent asks, decisions.
export function ReplyBriefCard({
  messageId,
  email,
  onClose,
}: {
  messageId?: string;
  email?: string;
  onClose?: () => void;
}): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinReplyBrief>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch on key change
  useEffect(() => {
    if (!messageId && !email) return;
    setBusy(true);
    setErr(null);
    api
      .addinReplyBrief({
        ...(messageId !== undefined ? { message_id: messageId } : {}),
        ...(email !== undefined ? { email } : {}),
      })
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load brief'))
      .finally(() => setBusy(false));
  }, [messageId, email]);

  if (busy && !data) return <Center p="sm"><Loader size="xs" /></Center>;
  if (err) return <Alert color="red" variant="light">{err}</Alert>;
  if (!data) return <></>;

  const snap = data.relationship_snapshot;
  const totalSignals =
    data.your_promises_to_them.length +
    data.their_promises_to_you.length +
    data.their_recent_asks.length +
    data.recent_decisions.length +
    data.unresolved_threads.length;

  return (
    <Card padding="sm" radius="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-indigo-6)' }}>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <IconBrain {...ICON_PROPS} />
          <Text fw={600} size="sm">Brief — before you reply</Text>
          {snap.is_vip && <Badge size="xs" color="yellow" leftSection={<IconStarFilled size={9} />}>VIP</Badge>}
          <Badge size="xs" variant="light">{totalSignals} signal{totalSignals === 1 ? '' : 's'}</Badge>
          {data.recent_decisions.length > 0 && (
            <Tooltip label={`${data.recent_decisions.length} decision${data.recent_decisions.length === 1 ? '' : 's'} on file`}>
              <Badge size="xs" color="teal" variant="light">
                {data.recent_decisions.length} dec
              </Badge>
            </Tooltip>
          )}
        </Group>
        <Group gap={4}>
          <Tooltip label="Copy brief as text">
            <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => {
              const lines: string[] = [];
              lines.push(`Brief — ${snap.email}${snap.is_vip ? ' (VIP)' : ''}`);
              lines.push(`${snap.total_emails} emails · known ${snap.days_known}d`);
              if (data.your_promises_to_them.length > 0) {
                lines.push('', `You owe them (${data.your_promises_to_them.length}):`);
                for (const p of data.your_promises_to_them) lines.push(`- ${p.promise_text}${p.due_at ? ` — due ${p.due_at.slice(0, 10)}` : ''}`);
              }
              if (data.their_promises_to_you.length > 0) {
                lines.push('', `They owe you (${data.their_promises_to_you.length}):`);
                for (const p of data.their_promises_to_you) lines.push(`- ${p.promise_text}${p.due_at ? ` — due ${p.due_at.slice(0, 10)}` : ''}`);
              }
              if (data.their_recent_asks.length > 0) {
                lines.push('', `Recent asks (${data.their_recent_asks.length}):`);
                for (const a of data.their_recent_asks) lines.push(`- ${a.subject ?? '(no subject)'}`);
              }
              if (data.recent_decisions.length > 0) {
                lines.push('', `Recent decisions (${data.recent_decisions.length}):`);
                for (const d of data.recent_decisions) lines.push(`- ${d.decision_text}`);
              }
              navigator.clipboard.writeText(lines.join('\n'))
                .then(() => notifications.show({ message: 'Brief copied.' }))
                .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
            }}>
              <IconFileText size={12} />
            </ActionIcon>
          </Tooltip>
          {onClose && (
            <ActionIcon size="sm" variant="subtle" onClick={onClose}>
              <IconX {...ICON_PROPS} />
            </ActionIcon>
          )}
        </Group>
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {snap.email} · {snap.total_emails} emails · known {snap.days_known}d
        {snap.last_contact_at ? ` · last ${formatRelative(snap.last_contact_at)}` : ''}
      </Text>

      {totalSignals === 0 ? (
        <Text size="xs" c="dimmed">No open promises, recent asks, or decisions on file. You're starting fresh.</Text>
      ) : (
        <Stack gap="xs">
          {data.your_promises_to_them.length > 0 && (
            <div>
              <Text size="xs" fw={600} c="orange" mb={2}>
                You owe them ({data.your_promises_to_them.length})
              </Text>
              <Stack gap={2}>
                {data.your_promises_to_them.slice(0, 4).map((p) => (
                  <Paper key={p.id} p={4} withBorder>
                    <Group gap={4} wrap="nowrap">
                      <Text size="xs" style={{ flex: 1 }} lineClamp={2}>{p.promise_text}</Text>
                      {p.due_at && (
                        <Badge size="xs" color={new Date(p.due_at) < new Date() ? 'red' : 'orange'}>
                          due {formatRelative(p.due_at)}
                        </Badge>
                      )}
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </div>
          )}

          {data.their_promises_to_you.length > 0 && (
            <div>
              <Text size="xs" fw={600} c="blue" mb={2}>
                They owe you ({data.their_promises_to_you.length})
              </Text>
              <Stack gap={2}>
                {data.their_promises_to_you.slice(0, 4).map((p) => (
                  <Paper key={p.id} p={4} withBorder>
                    <Group gap={4} wrap="nowrap">
                      <Text size="xs" style={{ flex: 1 }} lineClamp={2}>{p.promise_text}</Text>
                      {p.due_at && (
                        <Badge size="xs" color={new Date(p.due_at) < new Date() ? 'red' : 'blue'}>
                          due {formatRelative(p.due_at)}
                        </Badge>
                      )}
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </div>
          )}

          {data.their_recent_asks.length > 0 && (
            <div>
              <Text size="xs" fw={600} c="grape" mb={2}>
                Their recent asks ({data.their_recent_asks.length})
              </Text>
              <Stack gap={2}>
                {data.their_recent_asks.slice(0, 3).map((a) => (
                  <Paper key={a.message_id} p={4} withBorder>
                    <Text size="xs" fw={500} lineClamp={1}>{a.subject ?? '(no subject)'}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>{a.snippet}</Text>
                  </Paper>
                ))}
              </Stack>
            </div>
          )}

          {data.recent_decisions.length > 0 && (
            <div>
              <Text size="xs" fw={600} c="teal" mb={2}>
                Recent decisions ({data.recent_decisions.length})
              </Text>
              <Stack gap={2}>
                {data.recent_decisions.slice(0, 3).map((d) => (
                  <Paper key={d.id} p={4} withBorder>
                    <Text size="xs" lineClamp={2}>{d.decision_text}</Text>
                    <Text size="xs" c="dimmed">{formatRelative(d.decided_at)}</Text>
                  </Paper>
                ))}
              </Stack>
            </div>
          )}

          {data.unresolved_threads.length > 0 && (
            <div>
              <Text size="xs" fw={600} c="red" mb={2}>
                You sent last reply, no response ({data.unresolved_threads.length})
              </Text>
              <Stack gap={2}>
                {data.unresolved_threads.slice(0, 3).map((t) => (
                  <Paper key={t.message_id} p={4} withBorder>
                    <Text size="xs" lineClamp={1}>{t.subject ?? '(no subject)'}</Text>
                    <Text size="xs" c="dimmed">{formatRelative(t.received_at)}</Text>
                  </Paper>
                ))}
              </Stack>
            </div>
          )}
        </Stack>
      )}
    </Card>
  );
}

export function ReplyComposer({
  onDraft,
  loading,
  disabled,
  messageId,
}: {
  onDraft: (opts: {
    intent?: string;
    tone?: string;
    length?: string;
    user_intent?: string;
    constraints?: Record<string, boolean>;
  }) => void;
  loading: boolean;
  disabled: boolean;
  messageId?: string; // Phase 6b — enables "Brief me" button
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  // Brief auto-open preference — when set in settings, brief opens by default.
  const initialBriefOpen = (() => {
    try { return localStorage.getItem('addin_brief_auto_open') === '1'; } catch { return false; }
  })();
  const [briefOpen, setBriefOpen] = useState(initialBriefOpen);
  const [intent, setIntent] = useState('answer');
  const [tone, setTone] = useState('balanced');
  const [length, setLength] = useState('short');
  const [extra, setExtra] = useState('');
  const [noDeadlines, setNoDeadlines] = useState(false);
  const [noPricing, setNoPricing] = useState(false);
  const [noFault, setNoFault] = useState(false);

  function submit() {
    onDraft({
      intent,
      tone,
      length,
      ...(extra.trim() ? { user_intent: extra.trim() } : {}),
      constraints: {
        no_deadlines: noDeadlines,
        no_pricing: noPricing,
        no_admit_fault: noFault,
      },
    });
  }
  return (
    <Stack gap={4} style={{ width: '100%' }}>
      <Group gap="xs">
        <Button size="xs" onClick={submit} loading={loading} disabled={disabled}>
          Draft Reply
        </Button>
        {messageId && (
          <Button
            size="compact-xs"
            variant={briefOpen ? 'light' : 'subtle'}
            color="indigo"
            leftSection={<IconBrain size={12} />}
            onClick={() => setBriefOpen((v) => !v)}
          >
            {briefOpen ? 'Hide brief' : 'Brief me'}
          </Button>
        )}
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={() => setOpen((v) => !v)}
          rightSection={<span aria-hidden>{open ? '▾' : '▸'}</span>}
        >
          Compose options
        </Button>
      </Group>

      {messageId && briefOpen && (
        <ReplyBriefCard messageId={messageId} onClose={() => setBriefOpen(false)} />
      )}
      <Collapse in={open}>
        <Card padding="sm" radius="sm" withBorder mt={4}>
          <Stack gap="xs">
            <Group gap="xs" wrap="wrap">
              <Select
                label="Intent"
                size="xs"
                w={170}
                data={[
                  { value: 'answer', label: 'Answer' },
                  { value: 'acknowledge', label: 'Acknowledge' },
                  { value: 'clarify', label: 'Ask clarification' },
                  { value: 'apologize_delay', label: 'Apologize (delay)' },
                  { value: 'request_extension', label: 'Request extension' },
                  { value: 'escalate', label: 'Escalate internally' },
                  { value: 'decline', label: 'Decline politely' },
                  { value: 'confirm', label: 'Confirm receipt' },
                  { value: 'send_documents', label: 'Send documents' },
                  { value: 'negotiate', label: 'Negotiate' },
                  { value: 'request_meeting', label: 'Propose meeting' },
                  { value: 'pushback', label: 'Push back firmly' },
                  { value: 'free', label: 'Free-form' },
                ]}
                value={intent}
                onChange={(v) => v && setIntent(v)}
                allowDeselect={false}
              />
              <Select
                label="Tone"
                size="xs"
                w={150}
                data={[
                  { value: 'formal', label: 'Formal' },
                  { value: 'balanced', label: 'Balanced' },
                  { value: 'concise', label: 'Concise' },
                  { value: 'friendly', label: 'Friendly' },
                  { value: 'apologetic', label: 'Apologetic' },
                  { value: 'assertive', label: 'Assertive' },
                  { value: 'technical', label: 'Technical' },
                  { value: 'executive', label: 'Executive' },
                  { value: 'customer_support', label: 'Support' },
                  { value: 'legal_safe', label: 'Legal-safe' },
                ]}
                value={tone}
                onChange={(v) => v && setTone(v)}
                allowDeselect={false}
              />
              <Select
                label="Length"
                size="xs"
                w={130}
                data={[
                  { value: 'one_line', label: 'One line' },
                  { value: 'short', label: 'Short' },
                  { value: 'detailed', label: 'Detailed' },
                  { value: 'full', label: 'Full' },
                ]}
                value={length}
                onChange={(v) => v && setLength(v)}
                allowDeselect={false}
              />
            </Group>
            <Textarea
              autosize
              minRows={2}
              maxRows={4}
              size="xs"
              label="What I want to say (optional)"
              placeholder="e.g. tell them we'll send the documents Wednesday"
              value={extra}
              onChange={(e) => setExtra(e.currentTarget.value)}
            />
            <Group gap="md">
              <Checkbox
                size="xs"
                checked={noDeadlines}
                onChange={(e) => setNoDeadlines(e.currentTarget.checked)}
                label="No deadlines"
              />
              <Checkbox
                size="xs"
                checked={noPricing}
                onChange={(e) => setNoPricing(e.currentTarget.checked)}
                label="No pricing"
              />
              <Checkbox
                size="xs"
                checked={noFault}
                onChange={(e) => setNoFault(e.currentTarget.checked)}
                label="No fault admission"
              />
            </Group>
          </Stack>
        </Card>
      </Collapse>
    </Stack>
  );
}

export function SenderProfileDrawer({
  email,
  onClose,
  onJumpToSearch,
}: {
  email: string;
  onClose: () => void;
  onJumpToSearch?: (filter: { sender: string }) => void;
}): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinSenderProfile>> | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [vipBusy, setVipBusy] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  // Free-text notes per sender, persisted to localStorage. Survives reloads
  // and shared across all places this drawer opens (Action Inbox, search,
  // sender list, etc).
  const notesKey = `addin_sender_notes_${email.toLowerCase()}`;
  const [notes, setNotes] = useState<string>(() => {
    try { return localStorage.getItem(notesKey) ?? ''; } catch { return ''; }
  });
  const [notesSaved, setNotesSaved] = useState<number>(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load when email changes
  useEffect(() => {
    setBusy(true);
    setBriefOpen(false);
    try { setNotes(localStorage.getItem(notesKey) ?? ''); } catch { setNotes(''); }
    api
      .addinSenderProfile(email)
      .then(setData)
      .catch((e) => notifications.show({ color: 'red', message: friendlyAiError(e) }))
      .finally(() => setBusy(false));
  }, [email]);

  // Debounced save of notes to localStorage.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (notes.trim()) localStorage.setItem(notesKey, notes);
        else localStorage.removeItem(notesKey);
        setNotesSaved((n) => n + 1);
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
  }, [notes, notesKey]);

  async function toggleVip() {
    if (!data) return;
    setVipBusy(true);
    try {
      await api.addinVipSet(data.email, !data.is_vip);
      setData({ ...data, is_vip: !data.is_vip });
      notifications.show({ message: data.is_vip ? 'Removed from VIPs.' : 'Added to VIPs.' });
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally {
      setVipBusy(false);
    }
  }

  return (
    <Drawer
      opened={true}
      onClose={onClose}
      position="right"
      size="md"
      title={
        <Group gap="xs">
          <Text fw={600}>{email}</Text>
          {data?.is_vip && (
            <Badge color="yellow" variant="light" leftSection={<IconStarFilled size={10} />}>
              VIP
            </Badge>
          )}
          <Tooltip label="Copy email">
            <ActionIcon size="sm" variant="subtle" color="gray"
              onClick={() => {
                navigator.clipboard.writeText(email)
                  .then(() => notifications.show({ message: 'Email copied.' }))
                  .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
              }}>
              <IconFileText size={12} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
    >
      {busy && !data ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : !data ? (
        <Text c="dimmed">Could not load profile.</Text>
      ) : (
        <Stack gap="md">
          <Group>
            <Button
              size="xs"
              variant={data.is_vip ? 'light' : 'default'}
              color="yellow"
              loading={vipBusy}
              onClick={toggleVip}
              leftSection={
                data.is_vip ? <IconStarFilled size={14} /> : <IconStar size={14} />
              }
            >
              {data.is_vip ? 'VIP — remove' : 'Mark VIP'}
            </Button>
            <Button
              size="xs"
              variant="default"
              color="orange"
              leftSection={<IconArchive size={14} />}
              onClick={async () => {
                try {
                  await api.addinAntiVipAdd(data.email, 'sender-drawer');
                  notifications.show({ color: 'orange', message: `Muted ${data.email} (and purged existing mail).` });
                } catch (e) {
                  notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Mute failed' });
                }
              }}
            >
              Mute sender
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={async () => {
                if (!confirm(`Delete ALL ${data.total} indexed message${data.total === 1 ? '' : 's'} from ${data.email}?\n\nThis only removes them from the AI index; the originals stay in your Outlook mailbox. Sender is NOT muted by this action — future mail will index again.`)) return;
                try {
                  const r = await api.addinSenderDeleteAll(data.email);
                  notifications.show({ color: 'red', message: `Deleted ${r.deleted} message${r.deleted === 1 ? '' : 's'} from index.` });
                } catch (e) {
                  notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Delete failed' });
                }
              }}
            >
              Delete all from sender
            </Button>
            {onJumpToSearch && (
              <Button
                size="xs"
                variant="default"
                onClick={() => onJumpToSearch({ sender: data.email })}
              >
                See all emails →
              </Button>
            )}
            <Button
              size="xs"
              variant={briefOpen ? 'light' : 'default'}
              color="indigo"
              leftSection={<IconBrain size={14} />}
              onClick={() => setBriefOpen((v) => !v)}
            >
              {briefOpen ? 'Hide brief' : 'Brief me'}
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconFolder size={14} />}
              onClick={async () => {
                const name = prompt('Case name:', `Conversations with ${data.email}`);
                if (!name?.trim()) return;
                try {
                  const created = await api.addinCaseCreate({
                    name: name.trim(),
                    summary: `Auto-created from sender ${data.email}`,
                  });
                  const ids = data.recent_subjects.map((r) => r.message_id).slice(0, 50);
                  if (ids.length > 0) {
                    await api.addinCaseAddMessages(created.id, ids);
                  }
                  notifications.show({ color: 'green', message: `Created case + attached ${ids.length} message${ids.length === 1 ? '' : 's'}` });
                } catch (e) {
                  notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                }
              }}
            >
              Create case
            </Button>
          </Group>

          {briefOpen && (
            <ReplyBriefCard email={data.email} onClose={() => setBriefOpen(false)} />
          )}

          <Card padding="sm" radius="sm" withBorder>
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase">
                Total emails indexed
              </Text>
              <Text fw={700} size="xl">
                {data.total.toLocaleString()}
              </Text>
            </Stack>
          </Card>

          {/* Phase 6a — Relationship strength + symmetry */}
          <Card padding="sm" radius="sm" withBorder>
            <Stack gap="xs">
              <Group justify="space-between" align="flex-end">
                <Text size="xs" c="dimmed" tt="uppercase">Relationship</Text>
                <Badge size="lg"
                  color={data.relationship_strength >= 60 ? 'green' : data.relationship_strength >= 30 ? 'blue' : 'gray'}>
                  {data.relationship_strength}/100
                </Badge>
              </Group>
              <Progress value={data.relationship_strength}
                color={data.relationship_strength >= 60 ? 'green' : data.relationship_strength >= 30 ? 'blue' : 'gray'} />
              <SimpleGrid cols={2} spacing={4}>
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">From them</Text>
                  <Text size="sm" fw={500}>{data.total.toLocaleString()}</Text>
                </Stack>
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">From you to them</Text>
                  <Text size="sm" fw={500}>{data.sent_by_me_count.toLocaleString()}</Text>
                </Stack>
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Days known</Text>
                  <Text size="sm" fw={500}>{data.days_known.toLocaleString()}</Text>
                </Stack>
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Open cases</Text>
                  <Text size="sm" fw={500}>{data.open_cases_count}</Text>
                </Stack>
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Your avg reply</Text>
                  <Text size="sm" fw={500}>
                    {data.my_avg_reply_hours == null ? '—' : `${data.my_avg_reply_hours}h`}
                  </Text>
                </Stack>
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Their avg reply</Text>
                  <Text size="sm" fw={500}>
                    {data.their_avg_reply_hours == null ? '—' : `${data.their_avg_reply_hours}h`}
                  </Text>
                </Stack>
              </SimpleGrid>
            </Stack>
          </Card>

          {/* Free-text notes — persisted locally per sender */}
          <Card padding="sm" radius="sm" withBorder>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed" tt="uppercase">Notes</Text>
              <Group gap={4}>
                {notesSaved > 0 && notes.trim() && (
                  <Text size="xs" c="dimmed">Saved · {notes.length} chars</Text>
                )}
                {notes.trim() && (
                  <Tooltip label="Clear notes">
                    <ActionIcon size="xs" variant="subtle" color="gray"
                      onClick={() => { if (confirm('Clear notes for this sender?')) setNotes(''); }}>
                      <IconX size={10} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
            <Textarea
              size="xs"
              autosize
              minRows={2}
              maxRows={8}
              placeholder="Personal notes — preferred greeting, internal handle, context… Stays on this device."
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </Card>

          {/* Volume trend — last 6 weeks sparkline (cheap inline SVG) */}
          {data.volume_trend.some((v) => v.count > 0) && (
            <Card padding="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" mb={4}>Volume — last 6 weeks</Text>
              <Group gap={2} align="flex-end" h={48}>
                {data.volume_trend.map((v) => {
                  const max = Math.max(1, ...data.volume_trend.map((x) => x.count));
                  const pct = (v.count / max) * 100;
                  return (
                    <Tooltip key={v.week_start} label={`${v.week_start}: ${v.count}`}>
                      <div style={{
                        flex: 1,
                        height: `${Math.max(4, pct)}%`,
                        backgroundColor: 'var(--mantine-color-blue-5)',
                        borderRadius: 2,
                      }} />
                    </Tooltip>
                  );
                })}
              </Group>
            </Card>
          )}

          {/* Top topic phrases */}
          {data.topics_top.length > 0 && (
            <Card padding="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" mb={4}>What you talk about</Text>
              <Group gap={4}>
                {data.topics_top.map((t) => (
                  <Badge key={t.word} variant="light" size="sm">
                    {t.word} <Text component="span" c="dimmed" size="xs">·{t.count}</Text>
                  </Badge>
                ))}
              </Group>
            </Card>
          )}

          {data.last_contact && (
            <Card padding="sm" radius="sm" withBorder>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase">
                  Last contact
                </Text>
                <Text fw={500} size="sm" lineClamp={2}>
                  {data.last_contact.subject || '(no subject)'}
                </Text>
                <Text size="xs" c="dimmed">
                  {shortDate(data.last_contact.received_at)} ·{' '}
                  {formatRelative(data.last_contact.received_at)}
                </Text>
              </Stack>
            </Card>
          )}

          {data.tier_histogram.length > 0 && (
            <Card padding="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
                Classification mix
              </Text>
              <Stack gap={4}>
                {data.tier_histogram.map((t) => (
                  <Group key={t.tier} justify="space-between">
                    <Badge color={tierColor(t.tier)}>{t.tier}</Badge>
                    <Text size="sm">{t.n}</Text>
                  </Group>
                ))}
              </Stack>
            </Card>
          )}

          <Card padding="sm" radius="sm" withBorder>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed" tt="uppercase">Recent subjects</Text>
              <Button size="compact-xs" variant="subtle" leftSection={<IconFileText size={10} />}
                onClick={() => {
                  const lines = data.recent_subjects.map((r, i) =>
                    `${i + 1}. ${r.subject ?? '(no subject)'} — ${shortDate(r.received_at)}`,
                  ).join('\n');
                  navigator.clipboard.writeText(lines)
                    .then(() => notifications.show({ message: `Copied ${data.recent_subjects.length} subjects.` }))
                    .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
                }}>
                Copy
              </Button>
            </Group>
            <Stack gap={2}>
              {data.recent_subjects.map((r) => (
                <Group
                  key={r.message_id}
                  gap="xs"
                  wrap="nowrap"
                  onClick={() =>
                    postToHost({ type: 'open_in_outlook', message_id: r.message_id })
                  }
                  style={{ cursor: 'pointer', padding: 4, borderRadius: 4 }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-gray-1)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                >
                  {r.classification_tier && (
                    <Badge color={tierColor(r.classification_tier)} size="xs">
                      {r.classification_tier}
                    </Badge>
                  )}
                  {(() => {
                    const cs = cleanSubject(r.subject);
                    return (
                      <Group gap={4} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        {cs.prefix && <Badge size="xs" color="gray" variant="outline">{cs.prefix}</Badge>}
                        <Text size="sm" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>{cs.clean}</Text>
                      </Group>
                    );
                  })()}
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {shortDate(r.received_at)}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Card>
        </Stack>
      )}
    </Drawer>
  );
}

// Round 38: SecurityBadge — runs heuristic phishing/malware scan, shows
// red/yellow/green based on result, click expands to signal list.
export function SecurityBadge({ messageId }: { messageId: string }): React.JSX.Element {
  const [scan, setScan] = useState<Awaited<ReturnType<typeof api.addinSecurityScan>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load on message change
  useEffect(() => {
    setBusy(true);
    setScan(null);
    setExpanded(false);
    api
      .addinSecurityScan(messageId)
      .then(setScan)
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [messageId]);

  if (busy && !scan) return <Loader size="xs" />;
  if (!scan) return <></>;

  const color = scan.verdict === 'dangerous' ? 'red' : scan.verdict === 'suspicious' ? 'orange' : 'green';
  const label =
    scan.verdict === 'dangerous'
      ? `⚠ Danger (${scan.risk_score}/100)`
      : scan.verdict === 'suspicious'
        ? `Suspicious (${scan.risk_score}/100)`
        : 'Verified clean';
  return (
    <Stack gap={4} style={{ width: '100%' }}>
      <Group gap="xs">
        <Button
          size="compact-xs"
          color={color}
          variant="light"
          leftSection={<IconShield size={12} />}
          onClick={() => setExpanded((v) => !v)}
        >
          {label}
        </Button>
        {scan.signals.length > 0 && (
          <Text size="xs" c="dimmed">
            {scan.signals.length} signal{scan.signals.length === 1 ? '' : 's'}
          </Text>
        )}
        {scan.cached && (
          <Tooltip label="Result from cache — re-clicking is free">
            <Badge size="xs" variant="dot">
              cached
            </Badge>
          </Tooltip>
        )}
      </Group>
      {expanded && scan.signals.length > 0 && (
        <Card padding="xs" radius="sm" withBorder>
          <Stack gap={4}>
            {scan.signals.map((s, i) => (
              <Alert
                key={i}
                color={s.severity === 'high' ? 'red' : s.severity === 'medium' ? 'orange' : 'gray'}
                variant="light"
                p="xs"
                icon={<IconAlertTriangle size={14} />}
              >
                <Stack gap={0}>
                  <Text size="xs" fw={500}>
                    {s.type.replace(/_/g, ' ')}
                  </Text>
                  <Text size="xs">{s.detail}</Text>
                </Stack>
              </Alert>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

// Round 37: AttachmentsBadge — shows a paperclip + count if any. Click → drawer.
export function AttachmentsBadge({ messageId }: { messageId: string }): React.JSX.Element {
  const [count, setCount] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: messageId change reload
  useEffect(() => {
    api
      .addinAttachmentsForEmail(messageId)
      .then((r) => setCount(r.attachments.length))
      .catch(() => setCount(0));
  }, [messageId]);

  if (count == null) return <></>;
  if (count === 0) return <></>;
  return (
    <>
      <Button
        size="xs"
        variant="default"
        leftSection={<IconPaperclip {...ICON_PROPS} />}
        onClick={() => setDrawerOpen(true)}
      >
        {count} attachment{count === 1 ? '' : 's'}
      </Button>
      {drawerOpen && (
        <AttachmentsDrawer
          messageId={messageId}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}

export function AttachmentsDrawer({
  messageId,
  onClose,
}: {
  messageId: string;
  onClose: () => void;
}): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinAttachmentsForEmail>> | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<
    Record<string, Awaited<ReturnType<typeof api.addinAttachmentExtract>>>
  >({});

  async function load() {
    setBusy(true);
    try {
      setData(await api.addinAttachmentsForEmail(messageId));
    } finally {
      setBusy(false);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount
  useEffect(() => {
    load();
  }, []);

  async function extractOne(id: string) {
    setExtracting(id);
    try {
      const r = await api.addinAttachmentExtract(id);
      setExtracted((prev) => ({ ...prev, [id]: r }));
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally {
      setExtracting(null);
    }
  }

  function fmtSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  return (
    <Drawer
      opened={true}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="xs">
          <IconPaperclip {...ICON_PROPS} />
          <Text fw={600}>Attachments</Text>
        </Group>
      }
    >
      {busy && !data ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : !data || data.attachments.length === 0 ? (
        <Stack align="center" p="xl">
          <Text c="dimmed">No attachments indexed for this email yet.</Text>
          <Text size="xs" c="dimmed" ta="center" maw={400}>
            The C# addin uploads attachment bytes during indexing. If this email predates that
            change, attachments are not yet stored — re-index the folder to populate them.
          </Text>
        </Stack>
      ) : (
        <Stack gap="md">
          {data.attachments.map((a) => {
            const ai = extracted[a.id]
              ?? (a.ai_summary_json ? safeParse(a.ai_summary_json) : null);
            const isExtracting = extracting === a.id;
            return (
              <Card key={a.id} padding="md" radius="md" withBorder>
                <Stack gap="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs">
                        <IconFileText {...ICON_PROPS} />
                        <Text fw={500} size="sm" lineClamp={1}>
                          {a.filename}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {a.content_type ?? 'unknown type'} · {fmtSize(a.size_bytes)} ·{' '}
                        {a.extraction_status}
                      </Text>
                    </Stack>
                    {a.extracted_text_length > 0 && !ai && (
                      <Button
                        size="compact-xs"
                        leftSection={<IconSparkles size={12} />}
                        loading={isExtracting}
                        onClick={() => extractOne(a.id)}
                      >
                        AI extract
                      </Button>
                    )}
                  </Group>
                  {ai && (
                    <Card padding="sm" radius="sm" withBorder bg="var(--mantine-color-gray-0)">
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Badge color="blue" variant="light">
                            {ai.document_type ?? 'document'}
                          </Badge>
                          {ai._cache?.hit && (
                            <Badge size="xs" color="green" variant="light">
                              cached · $0
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm">{ai.summary}</Text>
                        {ai.amounts && ai.amounts.length > 0 && (
                          <Stack gap={2}>
                            <Text size="xs" fw={500} c="dimmed">
                              Amounts
                            </Text>
                            {ai.amounts.map((a, i) => (
                              <Text key={i} size="xs">
                                · {a.label}: <b>{a.value} {a.currency}</b>
                              </Text>
                            ))}
                          </Stack>
                        )}
                        {ai.dates && ai.dates.length > 0 && (
                          <Stack gap={2}>
                            <Text size="xs" fw={500} c="dimmed">
                              Dates
                            </Text>
                            {ai.dates.map((d, i) => (
                              <Text key={i} size="xs">
                                · {d.label}: <b>{d.value}</b>
                              </Text>
                            ))}
                          </Stack>
                        )}
                        {ai.parties && ai.parties.length > 0 && (
                          <Group gap={4}>
                            <Text size="xs" fw={500} c="dimmed">
                              Parties:
                            </Text>
                            {ai.parties.map((p, i) => (
                              <Badge key={i} size="xs" variant="outline">
                                {p}
                              </Badge>
                            ))}
                          </Group>
                        )}
                        {ai.risk_flags && ai.risk_flags.length > 0 && (
                          <Alert color="orange" variant="light" p="xs">
                            <Stack gap={2}>
                              {ai.risk_flags.map((r, i) => (
                                <Text key={i} size="xs">
                                  ⚠ {r.replace(/_/g, ' ')}
                                </Text>
                              ))}
                            </Stack>
                          </Alert>
                        )}
                      </Stack>
                    </Card>
                  )}
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}
    </Drawer>
  );
}
export function AddToCaseButton({ messageId }: { messageId: string }): React.JSX.Element {
  const [cases, setCases] = useState<{ id: string; name: string }[]>([]);
  const [opened, setOpened] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [newName, setNewName] = useState('');

  function openMenu() {
    if (!opened) {
      api.addinCasesList().then((r) =>
        setCases(r.cases.filter((c) => c.status === 'open').map((c) => ({ id: c.id, name: c.name }))),
      );
    }
    setOpened(true);
  }

  async function addToExisting(caseId: string) {
    await api.addinCaseAddMessages(caseId, [messageId]);
    notifications.show({ message: 'Added to case.' });
    setOpened(false);
  }

  async function createAndAdd() {
    if (!newName.trim()) return;
    const r = await api.addinCaseCreate({ name: newName.trim(), message_ids: [messageId] });
    notifications.show({ message: `Created case "${r.name}".` });
    setCreateMode(false);
    setNewName('');
    setOpened(false);
  }

  return (
    <Menu position="bottom-end" withArrow opened={opened} onChange={setOpened}>
      <Menu.Target>
        <Button size="xs" variant="default" onClick={openMenu}>
          Add to case
        </Button>
      </Menu.Target>
      <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
        {createMode ? (
          <Stack gap="xs" p="xs" style={{ minWidth: 240 }}>
            <Text size="xs" fw={500}>
              New case name
            </Text>
            <TextInput
              size="xs"
              value={newName}
              onChange={(e) => setNewName(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && createAndAdd()}
              autoFocus
            />
            <Group gap="xs" justify="flex-end">
              <Button size="compact-xs" variant="subtle" onClick={() => setCreateMode(false)}>
                Back
              </Button>
              <Button size="compact-xs" onClick={createAndAdd} disabled={!newName.trim()}>
                Create + add
              </Button>
            </Group>
          </Stack>
        ) : (
          <>
            <Menu.Label>Open cases</Menu.Label>
            {cases.length === 0 ? (
              <Menu.Item disabled>(no open cases)</Menu.Item>
            ) : (
              cases.map((c) => (
                <Menu.Item key={c.id} onClick={() => addToExisting(c.id)}>
                  {c.name}
                </Menu.Item>
              ))
            )}
            <Menu.Divider />
            <Menu.Item onClick={() => setCreateMode(true)}>+ Create new case…</Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

export function CommandBar({
  opened,
  onClose,
  onSubmitFilter,
}: {
  opened: boolean;
  onClose: () => void;
  onSubmitFilter: (filter: { q?: string; tier?: string; sender?: string; since?: string }) => void;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  async function submit() {
    if (!query.trim()) return;
    setBusy(true);
    setHint(null);
    try {
      const r = await api.addinCommand(query.trim());
      setHint(r.hint);
      onSubmitFilter(r.parsed);
      setQuery('');
    } catch (e) {
      setHint(e instanceof Error ? e.message : 'Command failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="AI command bar"
      size="lg"
      centered
      withCloseButton
    >
      <Stack gap="xs">
        <Text size="xs" c="dimmed">
          Type a natural-language query. Examples: <i>"P0 emails from this week"</i>,{' '}
          <i>"emails from connecti about contracts"</i>, <i>"high priority today"</i>.
        </Text>
        <TextInput
          size="md"
          placeholder="What do you want to find?"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') onClose();
          }}
          autoFocus
          rightSection={busy ? <Loader size="xs" /> : null}
        />
        {hint && (
          <Alert color="blue" variant="light">
            {hint}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy}>
            Run
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

